import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ShieldAlert, ArrowLeft, Pause, Play, Megaphone, AlertTriangle, CheckCircle2, Loader2, UserX } from "lucide-react";

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export default function SecureExamMonitor() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [snapshot, setSnapshot] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [broadcastText, setBroadcastText] = useState("");
  const [wsStatus, setWsStatus] = useState("connecting");
  const [helpRequests, setHelpRequests] = useState([]);
  const wsRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/secure-exams/${examId}/monitor`, { headers });
      setSnapshot(r.data);
    } catch (e) { toast.error("Failed to load monitor"); }
  };

  useEffect(() => { load(); }, [examId]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  // WebSocket for live updates
  useEffect(() => {
    if (!examId || !token) return;
    const wsUrl = `${API.replace(/^http/, "ws")}/ws/secure-exam-monitor/${examId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => { setWsStatus("open"); ws.send(JSON.stringify({ token })); };
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "connected") setWsStatus("connected");
        else if (msg.type === "incident" && msg.incident_type === "help_request") {
          const helpMsg = msg.meta?.message || "needs help";
          setHelpRequests(prev => [...prev, { student_name: msg.student_name, message: helpMsg, at: new Date().toLocaleTimeString() }]);
          toast.warning(`🆘 ${msg.student_name}: ${helpMsg}`, { duration: 15000 });
          load();
        }
        else if (msg.type === "student_started" || msg.type === "student_submitted" || msg.type === "student_progress" || msg.type === "incident") {
          load();
        } else if (msg.type === "exam_paused" || msg.type === "exam_resumed") {
          setSnapshot(s => s ? { ...s, paused: msg.type === "exam_paused" } : s);
        }
      } catch (e) { /* ignore */ }
    };
    ws.onclose = () => setWsStatus("closed");
    ws.onerror = () => setWsStatus("error");
    return () => { try { ws.close(); } catch (e) { /* noop */ } };
  }, [examId, token]);

  const pauseExam = async () => {
    try {
      await axios.post(`${API}/secure-exams/${examId}/pause`, {}, { headers });
      setSnapshot(s => ({ ...s, paused: true }));
      toast.success("Exam paused for all students");
    } catch (e) { toast.error("Pause failed"); }
  };
  const resumeExam = async () => {
    try {
      await axios.post(`${API}/secure-exams/${examId}/resume`, {}, { headers });
      setSnapshot(s => ({ ...s, paused: false }));
      toast.success("Exam resumed");
      load();
    } catch (e) { toast.error("Resume failed"); }
  };
  const sendBroadcast = async () => {
    if (!broadcastText.trim()) return;
    try {
      await axios.post(`${API}/secure-exams/${examId}/broadcast`, { message: broadcastText }, { headers });
      toast.success("Announcement sent");
      setBroadcastText("");
    } catch (e) { toast.error("Send failed"); }
  };
  const forceSubmit = async (attemptId) => {
    if (!window.confirm("Force-submit this student's paper?")) return;
    try {
      await axios.post(`${API}/secure-exams/${examId}/force-submit/${attemptId}`, {}, { headers });
      toast.success("Student force-submitted");
      load();
    } catch (e) { toast.error("Force-submit failed"); }
  };

  if (!snapshot) return <div className="p-6 text-sm text-slate-500">Loading...</div>;

  const inProgress = (snapshot.attempts || []).filter(a => a.status === "in_progress");
  const submitted = (snapshot.attempts || []).filter(a => a.status !== "in_progress");
  const totalWarnings = (a) => Object.values(a.warnings || {}).reduce((sum, v) => sum + v, 0);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="secure-exam-monitor">
      <div className="border-b border-slate-800 px-4 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/secure-exams")} className="text-white hover:bg-slate-800"><ArrowLeft className="w-4 h-4" /></Button>
          <ShieldAlert className="w-6 h-6 text-amber-400" />
          <div>
            <h1 className="text-lg font-bold">{snapshot.exam.title}</h1>
            <div className="text-[11px] text-slate-400 flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${wsStatus === "connected" ? "bg-green-400" : "bg-red-400"}`}></span>
              Live monitor · {inProgress.length} active · {submitted.length} submitted
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {snapshot.paused ? (
            <Button size="sm" onClick={resumeExam} className="bg-green-600 hover:bg-green-500" data-testid="resume-exam-btn"><Play className="w-4 h-4 mr-1" />Resume All</Button>
          ) : (
            <Button size="sm" onClick={pauseExam} className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="pause-exam-btn"><Pause className="w-4 h-4 mr-1" />Pause All</Button>
          )}
        </div>
      </div>

      {/* Broadcast */}
      <div className="border-b border-slate-800 px-4 py-3 flex items-center gap-2 max-w-3xl mx-auto w-full">
        <Megaphone className="w-4 h-4 text-amber-400" />
        <Input placeholder="Send announcement to all students (e.g., '5 minutes remaining')" value={broadcastText} onChange={(e) => setBroadcastText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendBroadcast(); }} className="bg-slate-900 border-slate-700 text-white" data-testid="broadcast-input" />
        <Button size="sm" onClick={sendBroadcast} disabled={!broadcastText.trim()} className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="send-broadcast-btn">Send</Button>
      </div>

      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
        {/* Help requests */}
        {helpRequests.length > 0 && (
          <div className="bg-sky-950/60 border border-sky-700 rounded-xl p-3 space-y-2" data-testid="help-requests-panel">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-sky-300 uppercase tracking-wide">🆘 Help Requests ({helpRequests.length})</div>
              <button onClick={() => setHelpRequests([])} className="text-[10px] text-sky-400 hover:text-sky-200 underline" data-testid="clear-help-requests">Clear all</button>
            </div>
            {helpRequests.slice(-5).reverse().map((h, i) => (
              <div key={i} className="text-xs text-sky-100 flex items-center gap-2">
                <span className="text-sky-400">{h.at}</span>
                <strong>{h.student_name}</strong>: {h.message}
              </div>
            ))}
          </div>
        )}
        {/* Grid */}
        <div>
          <h2 className="text-sm font-semibold text-slate-300 mb-3">Live Students ({inProgress.length})</h2>
          {inProgress.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500 bg-slate-900 rounded-xl border border-slate-800">
              No students have started yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {inProgress.map(a => {
                const warns = totalWarnings(a);
                const risk = warns >= 3 ? "high" : warns >= 1 ? "medium" : "low";
                const timeRem = a.time_ends_at ? Math.floor((new Date(a.time_ends_at).getTime() - now) / 1000) : 0;
                return (
                  <Card key={a.attempt_id} className={`p-4 bg-slate-900 border-slate-800 text-white ${risk === "high" ? "ring-2 ring-red-500" : risk === "medium" ? "ring-1 ring-amber-500" : ""}`} data-testid={`monitor-student-${a.attempt_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{a.student_name}</div>
                        <div className="text-[10px] text-slate-400">Section {(a.current_section || 0) + 1} · {a.answered_count || 0} answered</div>
                      </div>
                      {risk === "high" && <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" title="High risk"></span>}
                      {risk === "medium" && <span className="w-3 h-3 rounded-full bg-amber-500"></span>}
                      {risk === "low" && <span className="w-3 h-3 rounded-full bg-green-500"></span>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-300 mb-3">
                      <div>Time: {fmt(timeRem)}</div>
                      <div>Warnings: <strong className={risk === "high" ? "text-red-400" : risk === "medium" ? "text-amber-400" : ""}>{warns}</strong></div>
                    </div>
                    {warns > 0 && (
                      <div className="text-[10px] text-slate-400 space-y-0.5 mb-2">
                        {Object.entries(a.warnings || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                          <div key={k}>• {k.replace("_", " ")}: {v}</div>
                        ))}
                      </div>
                    )}
                    <Button size="sm" variant="outline" onClick={() => forceSubmit(a.attempt_id)} className="w-full bg-red-600 border-red-700 hover:bg-red-700 text-white" data-testid={`force-submit-${a.attempt_id}`}>
                      <UserX className="w-3.5 h-3.5 mr-1" />Force Submit
                    </Button>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Submitted */}
        {submitted.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-slate-300 mb-3">Submitted ({submitted.length})</h2>
            <div className="space-y-2">
              {submitted.map(a => (
                <div key={a.attempt_id} className="bg-slate-900 border border-slate-800 rounded-lg px-4 py-2.5 flex items-center justify-between">
                  <div className="text-sm">{a.student_name} — <span className="text-slate-400 text-xs">{a.status}</span></div>
                  <div className="text-sm font-mono">{a.score ?? "-"} / {a.total_marks ?? "-"}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
